// Angular
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  inject
} from '@angular/core';

// Material
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';

// Third party
import {
  Observable,
  Subscription,
  of,
  skip,
  takeUntil
} from 'rxjs';

// Local library
import {
  MatNotificationService,
  TranslatePipe,
  TranslationService,
  UnsubscribeObservableService
} from '@amwager/util';
import {
  IGeoLocationModel,
  IGetEventsDetailsResponse,
  isOnline$
} from '@amwager/util-ts';
import { CommonFacade, ENVIRONMENT } from '@amwager/data-access';
import { AccountFacade } from '@amwager/account/data-access';
import {
  HorseLoaderService,
  WagerConfirmDialogComponent,
  WagerConfirmDialogData
} from '@amwager/feature';
import {
  BetsDisplayService,
  BetsTab,
  IBetsEventGroup,
  IBetsGrouped,
  IBetsRaceGroup,
  ITicketSlim,
  WagerFacade
} from '@amwager/wager/data-access';

/**
 * My Bets — Saved / Active / Closed tabs with submit/remove/cancel actions
 */
@Component({
  selector: 'feature-mat-bets',
  templateUrl: './bets.component.html',
  styleUrls: ['./bets.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    TranslatePipe
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BetsComponent extends
  UnsubscribeObservableService implements OnInit, OnDestroy {

  private readonly HOST_COMPONENT = 'BetsComponent';

  /** Default tab to open on first paint */
  @Input() defaultTab: 'saved' | 'active' | 'closed' = 'active';

  showSavedBets: boolean = false;
  showActiveBets: boolean = true;
  showClosedBets: boolean = false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  savedBets$: Observable<any> = of([]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  activeBets$: Observable<any> = of([]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  closedBets$: Observable<any> = of([]);

  savedTickets: ITicketSlim[] = [];
  activeTickets: ITicketSlim[] = [];
  closedTickets: ITicketSlim[] = [];

  /** Grand Total -> Event -> Race -> Ticket tree for the My Bets table */
  grouped: IBetsGrouped = { events: [], grandTotalCount: 0, grandTotalAmount: 0 };

  isGrandTotalExpanded: boolean = true;
  expandedEvents = new Set<string>();
  expandedRaces = new Set<string>();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  events$: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  allEventTrackRace$: any;
  balanceAmount: number = 0;
  requireConfirmation: boolean = false;
  geoLocation!: IGeoLocationModel;
  isProcessing: boolean = false;
  isOnline: boolean = true;

  /** Submit-saved-bet error flags (reset each attempt) */
  isExceedsSubmitSaveBetErr: boolean = false;
  isGenericSubmitSaveBetErr: boolean = false;

  private isSubmitSavedBetSuccessSubs?: Subscription;
  private isRemoveSavedBetSuccessSubs?: Subscription;
  private isCancelBetSuccessSubs?: Subscription;

  /** Persistent auto-tab subs that flip tabs on successful save/bet-now */
  private isSaveBetSuccessAutoTabSubs?: Subscription;
  private isBetNowSuccessAutoTabSubs?: Subscription;

  readonly dialog = inject(MatDialog);

  constructor(
    public readonly betsDisplayService: BetsDisplayService,
    private readonly wagerFacade: WagerFacade,
    private readonly accountFacade: AccountFacade,
    private readonly commonFacade: CommonFacade,
    private readonly matNotificationService: MatNotificationService,
    private readonly horseLoaderService: HorseLoaderService,
    private readonly translationService: TranslationService,
    private readonly cdRef: ChangeDetectorRef,
    @Inject(ENVIRONMENT) readonly environment: string,
  ) {
    super();
  }

  ngOnInit(): void {
    this.checkConnectivity();

    this.applyDefaultTab();

    this.accountFacade.getBalance$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((res) => {
        if (res !== undefined) {
          this.balanceAmount = res;
        }
        this.cdRef.markForCheck();
      });

    this.wagerFacade.getSelectAllEventTrackRace$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((res: IGetEventsDetailsResponse[]) => {
        this.events$ = res;
        this.allEventTrackRace$ = res;
        this.refreshGroupedView();
        this.cdRef.markForCheck();
      });

    this.wagerFacade.getBets$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((response) => {
        if (response === undefined) return;

        const snapshot = this.betsDisplayService.parseBetsResponse(response);
        this.savedBets$ = snapshot.savedBets$;
        this.activeBets$ = snapshot.activeBets$;
        this.closedBets$ = snapshot.closedBets$;
        this.savedTickets = snapshot.savedTickets;
        this.activeTickets = snapshot.activeTickets;
        this.closedTickets = snapshot.closedTickets;

        this.refreshGroupedView();
        this.cdRef.markForCheck();
      });

    this.wagerFacade.getTicketGeoLocation$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((response?: IGeoLocationModel) => {
        if (response === undefined) return;
        this.geoLocation = response;
      });

    this.accountFacade.isGetAccountPreferencesSuccess$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((callback) => {
        if (callback === undefined) return;

        const response = callback?.response;
        this.requireConfirmation = response?.AccountPreferences?.RequireConfirmation;
        this.cdRef.markForCheck();
      });

    // Auto-tab on successful save-bet / bet-now responses. `skip(1)`
    // ignores the synchronous initial NgRx emit so we don't jump tabs
    // from stale success state left over from a previous session.
    this.isSaveBetSuccessAutoTabSubs?.unsubscribe();
    this.isSaveBetSuccessAutoTabSubs = this.wagerFacade.isSaveBetSuccess$()
      .pipe(
        skip(1),
        takeUntil(this.toUnsubscribe$)
      )
      .subscribe((callback) => {
        if (callback?.isSuccess !== true) return;
        this.savedTab();
      });

    this.isBetNowSuccessAutoTabSubs?.unsubscribe();
    this.isBetNowSuccessAutoTabSubs = this.wagerFacade.isBetNowSuccess$()
      .pipe(
        skip(1),
        takeUntil(this.toUnsubscribe$)
      )
      .subscribe((callback) => {
        if (callback?.isSuccess !== true) return;
        this.activeTab();
      });
  }

  override ngOnDestroy(): void {
    this.toUnsubscribe();

    this.isSubmitSavedBetSuccessSubs?.unsubscribe();
    this.isRemoveSavedBetSuccessSubs?.unsubscribe();
    this.isCancelBetSuccessSubs?.unsubscribe();
    this.isSaveBetSuccessAutoTabSubs?.unsubscribe();
    this.isBetNowSuccessAutoTabSubs?.unsubscribe();
  }

  /**
   * Pin the initial tab to `defaultTab`
   * @returns {void}
   */
  private applyDefaultTab(): void {
    this.applyTabFlags(this.defaultTab);
  }

  /**
   * Apply the show...Bets triplet flags for the given tab
   * @param {BetsTab} tab Tab to activate
   * @returns {void}
   */
  private applyTabFlags(tab: BetsTab): void {
    const flags = this.betsDisplayService.resolveTabFlags(tab);
    this.showSavedBets = flags.showSavedBets;
    this.showActiveBets = flags.showActiveBets;
    this.showClosedBets = flags.showClosedBets;
  }

  /**
   * Submit a saved bet to the wagering API
   * @param {ITicketSlim} ticket Saved ticket to submit
   * @param {'grand-total' | 'event' | 'race' | 'ticket'} row Clicked row level
   * @returns {void}
   */
  submitSavedBet(
    ticket: ITicketSlim,
    row: 'grand-total' | 'event' | 'race' | 'ticket' = 'ticket'
  ): void {
    if (this.isExceedsSubmitSaveBetErr) this.isExceedsSubmitSaveBetErr = false;
    if (this.isGenericSubmitSaveBetErr) this.isGenericSubmitSaveBetErr = false;

    // Single-race open check only applies to a concrete race/ticket; bulk
    // rows (grand-total/event) span multiple races and skip it
    if (
      (row === 'ticket' || row === 'race') &&
      !this.betsDisplayService.isRaceOpenForSubmit(ticket, this.allEventTrackRace$)
    ) {
      this.showAlertInfo('Info', 'Race is closed, no wagering permitted.', '', 'Close');
      return;
    }

    // if (!this.betsDisplayService.isRaceOpenForSubmit(ticket, this.allEventTrackRace$)) {
    //   this.showAlertInfo('Info', 'Race is closed, no wagering permitted.', '', 'Close');
    //   return;
    // }

    const ticketWithLocation: ITicketSlim = this.attachGeoLocation(ticket);

    this.horseLoaderService.show();

    this.wagerFacade.submitSavedBetAPI(ticketWithLocation);

    this.isSubmitSavedBetSuccessSubs?.unsubscribe();
    this.isSubmitSavedBetSuccessSubs = this.wagerFacade
      .isSubmitSavedBetSuccess$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((callback) => {
        if (callback?.isSuccess === undefined && callback?.response === undefined) {
          return;
        }

        if (callback?.response !== undefined) {
          const errorFlags = this.betsDisplayService
            .parseSubmitSavedBetErrors(callback);

          this.isExceedsSubmitSaveBetErr = errorFlags.isExceedsLossLimit;
          if (this.isExceedsSubmitSaveBetErr) {
            this.showAlertError('Exceeds loss limit.', '');
          }

          this.isGenericSubmitSaveBetErr = errorFlags.isGenericAdmin;
          if (this.isGenericSubmitSaveBetErr) {
            this.showAlertError(
              'Invalid ticket or wager. Please adjust selections.',
              ''
            );
          }
        }

        if (callback?.isSuccess) {
          this.isSubmitSavedBetSuccessSubs?.unsubscribe();
          this.activeTab();
        }

        this.horseLoaderService.hide();
      });
  }

  /**
   * Remove a saved bet from the wagering API
   * @param {ITicketSlim} ticket Saved ticket to remove
   * @returns {void}
   */
  removeSavedBet(ticket: ITicketSlim): void {
    this.horseLoaderService.show();

    this.wagerFacade.removeSavedBetAPI(ticket);

    this.isRemoveSavedBetSuccessSubs?.unsubscribe();
    this.isRemoveSavedBetSuccessSubs = this.wagerFacade
      .isRemoveSavedBetSuccess$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((callback) => {
        if (callback?.isSuccess === undefined && callback?.response === undefined) {
          return;
        }

        if (callback?.isSuccess) {
          this.isRemoveSavedBetSuccessSubs?.unsubscribe();
        }

        this.horseLoaderService.hide();
      });
  }

  /**
   * Cancel an active bet via the wagering API (isProcessing guard)
   * @param {ITicketSlim} ticket Active ticket to cancel
   * @returns {void}
   */
  cancelBet(ticket: ITicketSlim): void {
    if (this.isProcessing) return;

    this.isProcessing = true;

    const cancelTicket: ITicketSlim = this.betsDisplayService.buildCancelTicket(ticket);

    this.horseLoaderService.show();

    this.wagerFacade.cancelBetAPI(cancelTicket);

    this.isCancelBetSuccessSubs?.unsubscribe();
    this.isCancelBetSuccessSubs = this.wagerFacade
      .isCancelBetSuccess$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((callback) => {
        if (callback?.isSuccess === undefined && callback?.response === undefined) {
          return;
        }

        if (callback?.isSuccess) {
          this.isProcessing = false;
          this.showAlertInfo('Info', 'Tickets cancelled successfully', '', 'Close');
          this.isCancelBetSuccessSubs?.unsubscribe();
          this.cdRef.markForCheck();
          this.horseLoaderService.hide();
          return;
        }

        if (callback?.response !== undefined) {
          this.isProcessing = false;
          this.showAlertError(
            this.betsDisplayService.parseCancelBetError(callback),
            ''
          );
          this.isCancelBetSuccessSubs?.unsubscribe();
          this.cdRef.markForCheck();
          this.horseLoaderService.hide();
        }
      });
  }

  /**
   * Click handler for the cancel chip; opens confirm dialog if required
   * @param {ITicketSlim} ticket Active ticket to cancel
   * @returns {void}
   */
  confirmCancelBet(ticket: ITicketSlim): void {
    if (!this.isOnline) {
      this.commonFacade.setIsConnectedInternet(`${this.HOST_COMPONENT}:confirmCancelBet`, false);
      return;
    }

    if (ticket.IsCancelled) return;

    if (this.requireConfirmation) {
      this.openConfirmDialog('cancel-bet', 'Cancel Wager', ticket, () => {
        this.cancelBet(ticket);
      });
      return;
    }

    this.cancelBet(ticket);
  }

  /**
   * Resolve a clicked row into a representative ticket + the row's total.
   * Aggregated rows reuse the first available ticket for shared fields
   * (e.g. RunId) so the action can submit/remove by filter.
   * @param {'grand-total' | 'event' | 'race' | 'ticket'} row Clicked row level
   * @param {IBetsGrouped | IBetsEventGroup | IBetsRaceGroup | ITicketSlim} source Row data
   * @returns {{ ticket: ITicketSlim, totalAmount: number } | null} Context or null when empty
   */
  private resolveBetContext(
    row: 'grand-total' | 'event' | 'race' | 'ticket',
    source: IBetsGrouped | IBetsEventGroup | IBetsRaceGroup | ITicketSlim
  ): { ticket: ITicketSlim; totalAmount: number } | null {
    if (row === 'ticket') {
      const ticket = source as ITicketSlim;
      return ticket ? { ticket, totalAmount: ticket.TicketAmount } : null;
    }

    if (row === 'race') {
      const race = source as IBetsRaceGroup;
      const ticket = race?.tickets?.[0];
      return ticket ? { ticket, totalAmount: race.totalAmount } : null;
    }

    if (row === 'event') {
      const event = source as IBetsEventGroup;
      const ticket = event?.races?.[0]?.tickets?.[0];
      return ticket ? { ticket, totalAmount: event.totalAmount } : null;
    }

    const grouped = source as IBetsGrouped;
    const ticket = grouped?.events?.[0]?.races?.[0]?.tickets?.[0];
    return ticket ? { ticket, totalAmount: grouped.grandTotalAmount } : null;
  }

  /**
   * Click handler for submit/remove chips on any saved-bets row.
   * Broader rows (grand-total/event/race) act in bulk by filter, a leaf
   * ticket acts by its ids.
   * @param {string} betType 'submit-saved-bet' or 'remove-saved-bet'
   * @param {'grand-total' | 'event' | 'race' | 'ticket'} row Clicked row level
   * @param {IBetsGrouped | IBetsEventGroup | IBetsRaceGroup | ITicketSlim} source Row data
   * @returns {void}
   */
  confirmBet(
    betType: string,
    row: 'grand-total' | 'event' | 'race' | 'ticket',
    source: IBetsGrouped | IBetsEventGroup | IBetsRaceGroup | ITicketSlim
  ): void {
    if (!this.isOnline) {
      this.commonFacade.setIsConnectedInternet(`${this.HOST_COMPONENT}:confirmBet`, false);
      return;
    }

    // Resolve the clicked row into a representative ticket + its total
    const context = this.resolveBetContext(row, source);
    if (!context) return;

    const { ticket, totalAmount } = context;

    if (betType === 'submit-saved-bet' && totalAmount > this.balanceAmount) {
      this.showAlertInfo('Info', 'Insufficient Funds.', 'Deposit Now', 'Close');
      return;
    }

    // Scope ids to the clicked row; cleared ids are omitted by the effect so
    // broader rows submit/remove by filter. TicketAmount carries the row total
    // for the confirm dialog only (ignored by the API).
    let ticketPayload: ITicketSlim = {} as ITicketSlim;

    if (row === 'grand-total') {
      ticketPayload = {
        ...ticket,
        RunId: ticket.RunId,
        EventId: '',
        RaceId: 0,
        ParentId: '',
        TicketId: '',
        TicketAmount: totalAmount,
      };
    }

    if (row === 'event') {
      ticketPayload = {
        ...ticket,
        RunId: ticket.RunId,
        EventId: ticket.EventId,
        RaceId: 0,
        ParentId: '',
        TicketId: '',
        TicketAmount: totalAmount,
      };
    }

    if (row === 'race') {
      ticketPayload = {
        ...ticket,
        RunId: ticket.RunId,
        EventId: ticket.EventId,
        RaceId: ticket.RaceId,
        ParentId: '',
        TicketId: '',
        TicketAmount: totalAmount,
      };
    }

    if (row === 'ticket') {
      ticketPayload = {
        ...ticket,
        RunId: ticket.RunId,
        EventId: ticket.EventId,
        RaceId: ticket.RaceId,
        ParentId: ticket.ParentId,
        TicketId: ticket.TicketId,
      };
    }

    const runAction = () => {
      if (betType === 'submit-saved-bet') this.submitSavedBet(ticketPayload, row);
      else this.removeSavedBet(ticketPayload);
    };

    // Bulk rows always confirm with a summary message (legacy parity)
    if (row !== 'ticket') {
      const message = this.buildBulkConfirmMessage(betType, row, ticket);
      this.openConfirmDialog(betType, 'Please Confirm', ticketPayload, runAction, message);
      return;
    }

    if (this.requireConfirmation) {
      const title = betType === 'submit-saved-bet' ? 'Place Wager' : 'Remove Wager';
      this.openConfirmDialog(betType, title, ticketPayload, runAction);
      return;
    }

    runAction();

    // Old tail (single-ticket dialog only, kept for back up)
    // if (this.requireConfirmation) {
    //   const title = betType === 'submit-saved-bet' ? 'Place Wager' : 'Remove Wager';
    //   this.openConfirmDialog(betType, title, ticketPayload, () => {
    //     if (betType === 'submit-saved-bet') this.submitSavedBet(ticketPayload, row);
    //     else this.removeSavedBet(ticketPayload);
    //   });
    //   return;
    // }
    //
    // if (betType === 'submit-saved-bet') this.submitSavedBet(ticketPayload, row);
    // else this.removeSavedBet(ticketPayload);
  }

  /**
   * Build the confirm message for a bulk (grand-total/event/race) action,
   * mirroring the legacy "Are you sure you wish to ... all tickets ..." text.
   * @param {string} betType 'submit-saved-bet' or 'remove-saved-bet'
   * @param {'grand-total' | 'event' | 'race'} row Bulk row level
   * @param {ITicketSlim} ticket Representative ticket for event/race names
   * @returns {string} Confirmation message
   */
  private buildBulkConfirmMessage(
    betType: string,
    row: 'grand-total' | 'event' | 'race' | 'ticket',
    ticket: ITicketSlim
  ): string {
    // Keyed per action rather than interpolating the verb, which would leave it
    // in English inside a translated sentence.
    const action = betType === 'submit-saved-bet' ? 'submit' : 'remove';

    if (row === 'grand-total') {
      return this.translationService.translate(`ui.wager.confirm.${action}.all`);
    }

    const eventName = this.betsDisplayService.getEventName(
      ticket.EventId,
      this.allEventTrackRace$
    );

    if (row === 'event') {
      return this.translationService.translate(
        `ui.wager.confirm.${action}.event`,
        '',
        { event: eventName }
      );
    }

    return this.translationService.translate(`ui.wager.confirm.${action}.race`, '', {
      race: ticket.RaceId,
      event: eventName
    });
  }

  // Old single-ticket-only handler (kept for back up)
  // confirmBet(betType: string, row: 'grand-total' | 'event' | 'race' | 'ticket', ticket: ITicketSlim): void {
  //   if (!this.isOnline) {
  //     this.commonFacade.setIsConnectedInternet(`${this.HOST_COMPONENT}:confirmBet`, false);
  //     return;
  //   }
  //
  //   if (
  //     betType === 'submit-saved-bet' &&
  //     ticket.TicketAmount > this.balanceAmount
  //   ) {
  //     this.showAlertInfo('Info', 'Insufficient Funds.', 'Deposit Now', 'Close');
  //     return;
  //   }
  //
  //   let ticketPayload: ITicketSlim = {} as ITicketSlim;
  //
  //   if (row === 'grand-total') {
  //     ticketPayload = {
  //       ...ticket,
  //       RunId: ticket.RunId,
  //     }
  //   }
  //
  //   if (row === 'event') {
  //     ticketPayload = {
  //       ...ticket,
  //       RunId: ticket.RunId,
  //       EventId: ticket.EventId,
  //     }
  //   }
  //
  //   if (row === 'race') {
  //     ticketPayload = {
  //       ...ticket,
  //       RunId: ticket.RunId,
  //       EventId: ticket.EventId,
  //       RaceId: ticket.RaceId,
  //     }
  //   }
  //
  //   if (row === 'ticket') {
  //     ticketPayload = {
  //       ...ticket,
  //       RunId: ticket.RunId,
  //       EventId: ticket.EventId,
  //       RaceId: ticket.RaceId,
  //       ParentId: ticket.ParentId,
  //       TicketId: ticket.TicketId,
  //     }
  //   }
  //
  //   if (this.requireConfirmation) {
  //     const title = betType === 'submit-saved-bet' ? 'Place Wager' : 'Remove Wager';
  //     this.openConfirmDialog(betType, title, ticket, () => {
  //       if (betType === 'submit-saved-bet') this.submitSavedBet(ticketPayload);
  //       else this.removeSavedBet(ticketPayload);
  //     });
  //     return;
  //   }
  //
  //   if (betType === 'submit-saved-bet') this.submitSavedBet(ticketPayload);
  //   else this.removeSavedBet(ticketPayload);
  // }

  /**
   * Switch to the Saved tab.
   * @returns {void}
   */
  savedTab(): void {
    this.switchTab('saved');
  }

  /**
   * Switch to the Active tab.
   * @returns {void}
   */
  activeTab(): void {
    this.switchTab('active');
  }

  /**
   * Switch to the Closed tab.
   * @returns {void}
   */
  closedTab(): void {
    this.switchTab('closed');
  }

  /**
   * Shared tab-switch implementation
   * @param {BetsTab} tab Tab to switch to
   * @returns {void}
   */
  private switchTab(tab: BetsTab): void {
    this.applyTabFlags(tab);
    this.refreshGroupedView();
    this.cdRef.markForCheck();
  }

  /**
   * Currently-active tab name
   * @returns {'saved' | 'active' | 'closed'} Active tab
   */
  get currentTab(): 'saved' | 'active' | 'closed' {
    if (this.showSavedBets) return 'saved';
    if (this.showClosedBets) return 'closed';
    return 'active';
  }

  /**
   * Toggle the Grand Total row's expand state
   * @returns {void}
   */
  toggleGrandTotal(): void {
    this.isGrandTotalExpanded = !this.isGrandTotalExpanded;
    this.cdRef.markForCheck();
  }

  /**
   * Toggle an event row's expand state
   * @param {string} eventId Event whose row was clicked
   * @returns {void}
   */
  toggleEvent(eventId: string): void {
    if (this.expandedEvents.has(eventId)) {
      this.expandedEvents.delete(eventId);
    } else {
      this.expandedEvents.add(eventId);
    }
    this.cdRef.markForCheck();
  }

  /**
   * Toggle a race row's expand state
   * @param {string} raceKey Composite `eventId-raceId` key
   * @returns {void}
   */
  toggleRace(raceKey: string): void {
    if (this.expandedRaces.has(raceKey)) {
      this.expandedRaces.delete(raceKey);
    } else {
      this.expandedRaces.add(raceKey);
    }
    this.cdRef.markForCheck();
  }

  /**
   * Whether the supplied event row is currently expanded
   * @param {string} eventId Event row identifier
   * @returns {boolean} True when expanded
   */
  isEventExpanded(eventId: string): boolean {
    return this.expandedEvents.has(eventId);
  }

  /**
   * Whether the supplied race row is currently expanded
   * @param {string} raceKey Composite race row identifier
   * @returns {boolean} True when expanded
   */
  isRaceExpanded(raceKey: string): boolean {
    return this.expandedRaces.has(raceKey);
  }

  /**
   * Ticket details line (e.g. `$0.20 SPR 1/2/3/4`)
   * @param {ITicketSlim} ticket Ticket to summarise
   * @returns {string} Compact bet details line
   */
  toTicketDetailsLine(ticket: ITicketSlim): string {
    return this.betsDisplayService.toTicketDetailsLine(ticket);
  }

  /**
   * Ticket details line rendered in the ticket row, wrappable between legs
   * @param {ITicketSlim} ticket Ticket to summarise
   * @returns {string} Bet details line that wraps only at leg boundaries
   */
  toWrappableTicketDetailsLine(ticket: ITicketSlim): string {
    return this.betsDisplayService.toWrappableTicketDetailsLine(ticket);
  }

  /**
   * Recompute the grouped Grand Total → Event → Race → Ticket tree
   * @returns {void}
   */
  private refreshGroupedView(): void {
    const tickets = this.getCurrentTabTickets();
    this.grouped = this.betsDisplayService.groupBets(
      tickets,
      this.allEventTrackRace$
    );
  }

  /**
   * Ticket array driving the currently-active tab
   * @returns {ITicketSlim[]} Tickets for the active tab
   */
  private getCurrentTabTickets(): ITicketSlim[] {
    return this.betsDisplayService.getCurrentTabTickets(
      this.currentTab,
      this.savedTickets,
      this.activeTickets,
      this.closedTickets
    );
  }

  // Template wrappers over BetsDisplayService

  /**
   * Look up event name for the ticket's EventId
   * @param {string} eventId Ticket EventId
   * @returns {string} Human-readable event name
   */
  getEventName(eventId: string): string {
    return this.betsDisplayService.getEventName(eventId, this.events$);
  }

  /**
   * Grand total of non-cancelled tickets matching the status
   * @param {ITicketSlim[]} tickets Saved or active tickets
   * @param {number} ticketStatus 1=saved, 3=active, 6=closed
   * @returns {number} Grand total amount
   */
  getTotalAmount(tickets: ITicketSlim[], ticketStatus: number): number {
    return this.betsDisplayService.getTotalAmount(tickets, ticketStatus);
  }

  /**
   * Convert modifier name to two-letter abbreviation
   * @param {string} word Modifier name from the ticket
   * @returns {string} Abbreviated label or ''
   */
  convertWordsToAbbre(word: string): string {
    return this.betsDisplayService.convertWordsToAbbre(word);
  }

  /**
   * Collapse consecutive runner numbers into ranges
   * @param {string} runners Slash-separated runner numbers
   * @returns {string} Compact runners string
   */
  formatConsecutiveStringFE(runners: string): string {
    return this.betsDisplayService.formatConsecutiveStringFE(runners);
  }

  /**
   * Whether a closed ticket has a positive net payoff
   * @param {ITicketSlim} ticket Closed ticket
   * @returns {boolean} True when net payoff > 0
   */
  hasPositiveNetPayoff(ticket: ITicketSlim): boolean {
    return this.betsDisplayService.hasPositiveNetPayoff(ticket);
  }

  // Private helpers

  /**
   * Open the shared wager-confirm dialog and invoke onConfirm on confirm
   * @param {string} betType wager-confirm betType discriminator
   * @param {string} title Dialog header text
   * @param {ITicketSlim} ticket Ticket being confirmed
   * @param {() => void} onConfirm Invoked when the user confirms
   * @param {string} message Plain confirm text, used by the bulk-action rows
   * @returns {void}
   */
  private openConfirmDialog(
    betType: string,
    title: string,
    ticket: ITicketSlim,
    onConfirm: () => void,
    message?: string
  ): void {
    const eventName = this.betsDisplayService.getEventName(
      ticket?.EventId,
      this.allEventTrackRace$
    );
    const modifierAbbr = this.betsDisplayService.modifierAbbreviationFor(ticket?.Modifier);
    const runners = this.betsDisplayService.formatConsecutiveStringFE(ticket?.Runners);

    const dialogData: WagerConfirmDialogData = {
      betType,
      title,
      message,
      eventName,
      raceId: ticket?.RaceId ?? '',
      baseAmount: ticket?.BaseAmount ?? 0,
      poolId: ticket?.PoolId ?? '',
      modifier: ticket?.Modifier ?? '',
      modifierAbbr,
      runners,
      totalAmount: ticket?.TicketAmount,
    };

    const dialogRef = this.dialog.open(WagerConfirmDialogComponent, {
      data: dialogData,
      width: '340px',
      maxWidth: '95vw',
      autoFocus: false,
      disableClose: true,
      enterAnimationDuration: '120ms',
      exitAnimationDuration: '0ms',
      panelClass: 'wager-confirm-modal-dialog',
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((confirmed: boolean | undefined) => {
        if (!confirmed) return;
        onConfirm();
      });
  }

  /**
   * Attach cached geo-location to the ticket when both coords are valid
   * @param {ITicketSlim} ticket Ticket to enrich
   * @returns {ITicketSlim} Ticket with Location, or the same ticket
   */
  private attachGeoLocation(ticket: ITicketSlim): ITicketSlim {
    return this.betsDisplayService.attachGeoLocation(ticket, this.geoLocation);
  }

  /**
   * Sync browser online/offline state into CommonFacade
   * @returns {void}
   */
  private checkConnectivity(): void {
    isOnline$(this.environment)
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((online: boolean) => {
        this.isOnline = online;
        this.commonFacade.setIsConnectedInternet(
          `${this.HOST_COMPONENT}:checkConnectivity`,
          online
        );
        this.cdRef.markForCheck();
      });
  }

  /**
   * Show an info alert via MatNotificationService
   * @param {string} title Notification title
   * @param {string} message Notification body
   * @param {string} positiveText Optional positive action label
   * @param {string} negativeText Negative action label (defaults to 'Close')
   * @returns {void}
   */
  private showAlertInfo(
    title: string,
    message: string,
    positiveText: string = '',
    negativeText: string = 'Close'
  ): void {
    this.matNotificationService.showAlert(
      title,
      message,
      '',
      positiveText,
      negativeText
    );
  }

  /**
   * Show an error alert via MatNotificationService
   * @param {string} message Error message
   * @param {string} positiveText Optional positive action label
   * @returns {void}
   */
  private showAlertError(message: string, positiveText: string = ''): void {
    this.matNotificationService.showAlert(
      'Error',
      message,
      '',
      positiveText,
      'Close',
      '',
      true
    );
  }
}
